import React, { useState } from 'react';
import { createCandidature } from './api';
import { FaThumbsUp } from 'react-icons/fa';
import TurnstileWidget from './TurnstileWidget';
import RecaptchaWidget from './RecaptchaWidget';
import { toast } from './Toast';
import { logClientError } from './logClient';
import {
  executeRecaptchaEnterprise,
  RECAPTCHA_ACTION,
} from './recaptchaEnterprise';
import {
  INITIAL_FORM_DATA,
  validateCandidatureForm,
  buildCandidaturePayload,
} from './form/candidatureForm';

const CAPTCHA_SITE_KEY =
  process.env.REACT_APP_CAPTCHA_SITE_KEY ||
  process.env.REACT_APP_TURNSTILE_SITE_KEY ||
  '';

function resolveCaptchaProvider() {
  const raw = (process.env.REACT_APP_CAPTCHA_PROVIDER || '').toLowerCase();
  if (CAPTCHA_SITE_KEY.startsWith('0x')) return 'turnstile';
  // Clés Google Cloud Console (6L…) → Enterprise score (pas v2 checkbox)
  if (CAPTCHA_SITE_KEY.startsWith('6L')) return 'recaptcha_enterprise';
  if (raw === 'turnstile' || raw === 'recaptcha' || raw === 'recaptcha_enterprise') {
    return raw;
  }
  return raw || 'turnstile';
}

const CAPTCHA_PROVIDER = resolveCaptchaProvider();
const IS_ENTERPRISE = CAPTCHA_PROVIDER === 'recaptcha_enterprise';
const IS_TURNSTILE = CAPTCHA_PROVIDER === 'turnstile';

export default function PublicCandidatureForm() {
  const [formData, setFormData] = useState(() => ({ ...INITIAL_FORM_DATA, posteSouhaite: { ...INITIAL_FORM_DATA.posteSouhaite }, disponibilitePreference: { ...INITIAL_FORM_DATA.disponibilitePreference } }));

  const [errors, setErrors] = useState({});
  const [honeypot, setHoneypot] = useState({ website: '', company: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    
    if (type === 'checkbox' && name.startsWith('posteSouhaite_')) {
      const posteKey = name.replace('posteSouhaite_', '');
      setFormData(prev => ({
        ...prev,
        posteSouhaite: {
          ...prev.posteSouhaite,
          [posteKey]: checked
        }
      }));
    } else if (type === 'checkbox' && name.startsWith('disponibilitePreference_')) {
      const preferenceKey = name.replace('disponibilitePreference_', '');
      setFormData(prev => ({
        ...prev,
        disponibilitePreference: {
          ...prev.disponibilitePreference,
          [preferenceKey]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'file' ? files[0] : value
      }));
    }

    // Effacer l'erreur pour ce champ quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = validateCandidatureForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        setSubmitting(true);
        let token = captchaToken;
        if (IS_ENTERPRISE) {
          if (!CAPTCHA_SITE_KEY) {
            throw new Error('Captcha non configuré');
          }
          token = await executeRecaptchaEnterprise(CAPTCHA_SITE_KEY, RECAPTCHA_ACTION);
          setCaptchaToken(token);
        }
        const candidatureData = buildCandidaturePayload(formData, {
          honeypot,
          captchaToken: token,
        });

        await createCandidature(candidatureData);
        toast('Formulaire soumis avec succès ! Merci pour votre candidature.', 'success');
        
        // Réinitialiser le formulaire
        setHoneypot({ website: '', company: '' });
        setCaptchaToken('');
        setFormData({
          nom: '',
          prenom: '',
          telephone: '',
          dateNaissance: '',
          ville: '',
          quartier: '',
          posteSouhaite: {
            plomberie: false,
            peinture: false,
            electricite: false,
            froidClimatisation: false,
            autre: false
          },
          autreDomaine: '',
          niveauScolarisation: '',
          formationProfessionnelle: '',
          anneesExperience: '',
          niveauCompetence: '',
          lecturePlans: '',
          capaciteInstructions: '',
          materielTravail: '',
          disponibiliteJours: '',
          smartphone: '',
          accesInternetMobile: '',
          moyenDeplacement: '',
          autreMoyenDeplacement: '',
          disponibilitePreference: {
            joursOuvrables: false,
            soirs: false,
            weekend: false
          }
        });
      } catch (error) {
        logClientError('soumission candidature', error);
        const msg =
          error?.message ||
          'Erreur lors de la soumission du formulaire. Veuillez réessayer.';
        toast(msg, 'error');
      } finally {
        setSubmitting(false);
      }
    } else {
      toast('Veuillez corriger les erreurs dans le formulaire.', 'warning');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-blue-200 to-blue-100 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 mb-4">
          <div className="flex justify-between items-center">
            <div className="bg-blue-600 text-white py-2 px-4 rounded-lg text-center text-sm font-medium shadow-md">
              Section 1 of 2
            </div>
          </div>
        </div>

        {/* Section 1: Header et image du technicien */}
        <div className="space-y-6">
          {/* Carte du header */} 
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-500 active:ring-2 active:ring-blue-500">
            <h1 className="text-3xl font-bold text-gray-800 mb-3">
              Formulaire de Candidature - Technicien <span>ICA</span> mobile
            </h1>
            <p className="text-gray-600 text-base leading-relaxed">
              Veuillez remplir ce formulaire pour postuler à un poste de technicien qualifié au Burkina Faso.
            </p>
          </div>

          {/* Section publicitaire */}
          <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              <span>Ingenious City</span>
            </h2>
            <div className="relative rounded-lg overflow-hidden bg-gradient-to-r from-blue-500 to-green-500 min-h-[300px] flex items-end">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>
              <img
                src="/technicien.png"
                alt="Technicien Ingenious City"
                className="absolute left-0 bottom-0 w-[100%] h-[100%] object-cover object-left-center"
              />
             
            </div>
          </div>
        </div>

        {/* Section 2: Formulaire de recrutement */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot anti-bot — ne pas remplir */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            <label htmlFor="website">Site web</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot.website}
              onChange={(e) => setHoneypot((h) => ({ ...h, website: e.target.value }))}
            />
            <label htmlFor="company">Société</label>
            <input
              type="text"
              id="company"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot.company}
              onChange={(e) => setHoneypot((h) => ({ ...h, company: e.target.value }))}
            />
          </div>
          {/* Informations Personnelles */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Informations Personnelles</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">Nom </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.nom ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.nom && <span className="text-red-500 text-xs mt-1 block">{errors.nom}</span>}
              </div>
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">Prénom </label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.prenom ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.prenom && <span className="text-red-500 text-xs mt-1 block">{errors.prenom}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">Numéro de téléphone </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.telephone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: +226 XX XX XX XX"
                  required
                />
                {errors.telephone && <span className="text-red-500 text-xs mt-1 block">{errors.telephone}</span>}
              </div>
              <div>
                <label htmlFor="dateNaissance" className="block text-sm font-medium text-gray-700 mb-2">Date de naissance </label>
                <input
                  type="date"
                  id="dateNaissance"
                  name="dateNaissance"
                  value={formData.dateNaissance}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.dateNaissance ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.dateNaissance && <span className="text-red-500 text-xs mt-1 block">{errors.dateNaissance}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label htmlFor="ville" className="block text-sm font-medium text-gray-700 mb-2">Ville </label>
                <input
                  type="text"
                  id="ville"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.ville ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Ouagadougou"
                  required
                />
                {errors.ville && <span className="text-red-500 text-xs mt-1 block">{errors.ville}</span>}
              </div>
              <div>
                <label htmlFor="quartier" className="block text-sm font-medium text-gray-700 mb-2">Quartier </label>
                <input
                  type="text"
                  id="quartier"
                  name="quartier"
                  value={formData.quartier}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                    errors.quartier ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Zone du bois"
                  required
                />
                {errors.quartier && <span className="text-red-500 text-xs mt-1 block">{errors.quartier}</span>}
              </div>
            </div>
          </div>

          {/* Poste de Technicien Souhaité */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Poste de Technicien Souhaité</h2>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="posteSouhaite_plomberie"
                    name="posteSouhaite_plomberie"
                    checked={formData.posteSouhaite.plomberie}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                  />
                  <label htmlFor="posteSouhaite_plomberie" className="text-gray-700 cursor-pointer">Plomberie</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="posteSouhaite_peinture"
                    name="posteSouhaite_peinture"
                    checked={formData.posteSouhaite.peinture}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                  />
                  <label htmlFor="posteSouhaite_peinture" className="text-gray-700 cursor-pointer">Peinture</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="posteSouhaite_electricite"
                    name="posteSouhaite_electricite"
                    checked={formData.posteSouhaite.electricite}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                  />
                  <label htmlFor="posteSouhaite_electricite" className="text-gray-700 cursor-pointer">Électricité</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="posteSouhaite_froidClimatisation"
                    name="posteSouhaite_froidClimatisation"
                    checked={formData.posteSouhaite.froidClimatisation}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                  />
                  <label htmlFor="posteSouhaite_froidClimatisation" className="text-gray-700 cursor-pointer">Froid / Climatisation</label>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="checkbox"
                    id="posteSouhaite_autre"
                    name="posteSouhaite_autre"
                    checked={formData.posteSouhaite.autre}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                  />
                  <label htmlFor="posteSouhaite_autre" className="text-gray-700 cursor-pointer">Autre (Préciser ci-dessous)</label>
                </div>
              </div>
            {errors.posteSouhaite && <span className="text-red-500 text-xs mt-2 block">{errors.posteSouhaite}</span>}
          </div>

          {/* Si Autre, préciser */}
          {formData.posteSouhaite.autre && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Si 'Autre', veuillez préciser le domaine technique :</h2>
              <div>
                <input
                  type="text"
                  id="autreDomaine"
                  name="autreDomaine"
                  value={formData.autreDomaine}
                  onChange={handleChange}
                  className={`w-full border-b-2 border-dashed border-gray-300 bg-transparent px-0 py-2 focus:outline-none focus:border-blue-400 transition-colors ${
                    errors.autreDomaine ? 'border-red-500' : ''
                  }`}
                  placeholder="Short answer text"
                />
                {errors.autreDomaine && <span className="text-red-500 text-xs mt-1 block">{errors.autreDomaine}</span>}
              </div>
            </div>
          )}

          {/* Niveau de Scolarisation */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Quel est votre niveau de scolarisation le plus élevé atteint ?</h2>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_aucun"
                    name="niveauScolarisation"
                    value="aucun"
                    checked={formData.niveauScolarisation === 'aucun'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_aucun" className="text-gray-700 cursor-pointer">Aucun ou Alphabetisation</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_cep"
                    name="niveauScolarisation"
                    value="cep"
                    checked={formData.niveauScolarisation === 'cep'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_cep" className="text-gray-700 cursor-pointer">CEP (Certificat d'Études Primaires)</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_bepc"
                    name="niveauScolarisation"
                    value="bepc"
                    checked={formData.niveauScolarisation === 'bepc'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_bepc" className="text-gray-700 cursor-pointer">BEPC (Brevet d'Études du Premier Cycle)</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_cap"
                    name="niveauScolarisation"
                    value="cap"
                    checked={formData.niveauScolarisation === 'cap'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_cap" className="text-gray-700 cursor-pointer">CAP/CQP (Certificat d'Aptitude Professionnelle / Certificat de Qualification Professionnelle)</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_bac"
                    name="niveauScolarisation"
                    value="bac"
                    checked={formData.niveauScolarisation === 'bac'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_bac" className="text-gray-700 cursor-pointer">Baccalauréat</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    id="niveauScolarisation_universitaire"
                    name="niveauScolarisation"
                    value="universitaire"
                    checked={formData.niveauScolarisation === 'universitaire'}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
                  />
                  <label htmlFor="niveauScolarisation_universitaire" className="text-gray-700 cursor-pointer">Diplôme Universitaire (Licence, Master, etc.)</label>
                </div>
              </div>
            {errors.niveauScolarisation && <span className="text-red-500 text-xs mt-2 block">{errors.niveauScolarisation}</span>}
          </div>

          {/* Formation Professionnelle */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Avez-vous une formation professionnelle spécifique dans le domaine technique choisi ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="formationProfessionnelle_diplome"
                name="formationProfessionnelle"
                value="diplome"
                checked={formData.formationProfessionnelle === 'diplome'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="formationProfessionnelle_diplome" className="text-gray-700 cursor-pointer">Oui, j'ai un diplôme / certificat (CAP, CQP, etc.)</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="formationProfessionnelle_formation"
                name="formationProfessionnelle"
                value="formation"
                checked={formData.formationProfessionnelle === 'formation'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="formationProfessionnelle_formation" className="text-gray-700 cursor-pointer">Oui, j'ai suivi une formation non-diplômante (atelier, apprentissage sur le tas, etc.)</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="formationProfessionnelle_pratique"
                name="formationProfessionnelle"
                value="pratique"
                checked={formData.formationProfessionnelle === 'pratique'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="formationProfessionnelle_pratique" className="text-gray-700 cursor-pointer">Non, mon expérience est basée sur l'apprentissage pratique</label>
            </div>
          </div>
            {errors.formationProfessionnelle && <span className="text-red-500 text-xs mt-1 block">{errors.formationProfessionnelle}</span>}
          </div>

          {/* Années d'Expérience */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Depuis combien d'années exercez-vous ce métier ou un métier similaire ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="anneesExperience_moins1"
                name="anneesExperience"
                value="moins1"
                checked={formData.anneesExperience === 'moins1'}
                onChange={handleChange}
              />
              <label htmlFor="anneesExperience_moins1"> Moins de 1 an</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="anneesExperience_1a3"
                name="anneesExperience"
                value="1a3"
                checked={formData.anneesExperience === '1a3'}
                onChange={handleChange}
              />
              <label htmlFor="anneesExperience_1a3">1 à 3 ans</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="anneesExperience_4a7"
                name="anneesExperience"
                value="4a7"
                checked={formData.anneesExperience === '4a7'}
                onChange={handleChange}
              />
              <label htmlFor="anneesExperience_4a7"> 4 à 7 ans</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="anneesExperience_8a12"
                name="anneesExperience"
                value="8a12"
                checked={formData.anneesExperience === '8a12'}
                onChange={handleChange}
              />
              <label htmlFor="anneesExperience_8a12"> 8 à 12 ans</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="anneesExperience_plus12"
                name="anneesExperience"
                value="plus12"
                checked={formData.anneesExperience === 'plus12'}
                onChange={handleChange}
              />
              <label htmlFor="anneesExperience_plus12"> Plus de 12 ans</label>
            </div>
          </div>
            {errors.anneesExperience && <span className="text-red-500 text-xs mt-1 block">{errors.anneesExperience}</span>}
          </div>

          {/* Niveau de Compétence Pratique */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Veuillez évaluer votre niveau de compétence pratique dans le domaine choisi :</h2>

          <div className="mb-4">
            <select
              id="niveauCompetence"
              name="niveauCompetence"
              value={formData.niveauCompetence}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                      errors.niveauCompetence ? 'border-red-500' : 'border-gray-300'
                    }`}
              required
            >
              <option value="">Sélectionnez votre niveau de compétence...</option>
              <option value="1">1 - Débutant/Peu expérimenté</option>
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num.toString()}>
                  {num} - Expert/Très compétent
                </option>
              ))}
            </select>
            {errors.niveauCompetence && <span className="text-red-500 text-xs mt-1 block">{errors.niveauCompetence}</span>}
          </div>
          </div>

          {/* Lecture de Plans */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Êtes-vous capable de lire et d'interpréter des plans ou des schémas techniques simples liés à votre métier ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="lecturePlans_oui_sans_probleme"
                name="lecturePlans"
                value="oui_sans_probleme"
                checked={formData.lecturePlans === 'oui_sans_probleme'}
                onChange={handleChange}
              />
              <label htmlFor="lecturePlans_oui_sans_probleme">Oui, sans problème</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="lecturePlans_oui_aide"
                name="lecturePlans"
                value="oui_aide"
                checked={formData.lecturePlans === 'oui_aide'}
                onChange={handleChange}
              />
              <label htmlFor="lecturePlans_oui_aide">Oui, avec un peu d'aide/difficulté</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="lecturePlans_non"
                name="lecturePlans"
                value="non"
                checked={formData.lecturePlans === 'non'}
                onChange={handleChange}
              />
              <label htmlFor="lecturePlans_non">Non, pas du tout</label>
            </div>
          </div>
            {errors.lecturePlans && <span className="text-red-500 text-xs mt-1 block">{errors.lecturePlans}</span>}
          </div>

          {/* Capacité à suivre des instructions */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Évaluez votre capacité à suivre des instructions écrites et orales pour l'exécution d'un travail :</h2>

          <div className="mb-4">
            <select
              id="capaciteInstructions"
              name="capaciteInstructions"
              value={formData.capaciteInstructions}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors ${
                      errors.capaciteInstructions ? 'border-red-500' : 'border-gray-300'
                    }`}
              required
            >
              <option value="">Sélectionnez une valeur de 1 à 10...</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num.toString()}>
                  {num}
                </option>
              ))}
            </select>
            {errors.capaciteInstructions && <span className="text-red-500 text-xs mt-1 block">{errors.capaciteInstructions}</span>}
          </div>

          {/* Affichage dynamique des icônes thumbs up */}
          {formData.capaciteInstructions && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400">
              <div className="flex justify-center items-center gap-3 flex-wrap">
                {Array.from({ length: parseInt(formData.capaciteInstructions) || 0 }, (_, index) => (
                  <FaThumbsUp key={index} className="text-4xl text-blue-600 animate-fade-in" />
                ))}
              </div>
            </div>
          )}
        </div>

          {/* Indicateur Section 2 */}
          <div className="bg-blue-600 text-white py-2 px-4 rounded-t-lg text-center text-sm font-medium shadow-md">
            Section 2 of 2
          </div>

          {/* Section sans titre */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Section sans titre</h2>
            <p className="text-gray-600 text-sm italic mt-2">Description (optional)</p>
          </div>

          {/* Matériel de travail */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Avez-vous votre propre matériel de travail de base (outils principaux) ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="materielTravail_bien_equipe"
                name="materielTravail"
                value="bien_equipe"
                checked={formData.materielTravail === 'bien_equipe'}
                onChange={handleChange}
              />
              <label htmlFor="materielTravail_bien_equipe">Oui, je suis bien équipé</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="materielTravail_minimum"
                name="materielTravail"
                value="minimum"
                checked={formData.materielTravail === 'minimum'}
                onChange={handleChange}
              />
              <label htmlFor="materielTravail_minimum">Oui, j'ai le minimum</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="materielTravail_non"
                name="materielTravail"
                value="non"
                checked={formData.materielTravail === 'non'}
                onChange={handleChange}
              />
              <label htmlFor="materielTravail_non">Non, je n'ai pas d'outils</label>
            </div>
          </div>
            {errors.materielTravail && <span className="text-red-500 text-xs mt-1 block">{errors.materielTravail}</span>}
          </div>

          {/* Disponibilité pour commencer */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Disponibilité pour commencer le travail (en jours après l'entretien) :</h2>

          <div className="mb-4">
            <input
              type="text"
              id="disponibiliteJours"
              name="disponibiliteJours"
              value={formData.disponibiliteJours}
              onChange={handleChange}
              className={`w-full border-b-2 border-dashed bg-transparent px-0 py-2 focus:outline-none focus:border-blue-400 transition-colors italic placeholder-gray-400 ${
                      errors.disponibiliteJours ? 'border-red-500' : 'border-gray-300'
                    }`}
              placeholder="Short answer text"
            />
            {errors.disponibiliteJours && <span className="text-red-500 text-xs mt-1 block">{errors.disponibiliteJours}</span>}
          </div>
          </div>

          {/* Smartphone */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Disposez-vous d'un Smartphone fonctionnel (Android ou <span>iOS</span>) ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="smartphone_oui"
                name="smartphone"
                value="oui"
                checked={formData.smartphone === 'oui'}
                onChange={handleChange}
              />
              <label htmlFor="smartphone_oui">oui</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="smartphone_non"
                name="smartphone"
                value="non"
                checked={formData.smartphone === 'non'}
                onChange={handleChange}
              />
              <label htmlFor="smartphone_non">Non</label>
            </div>
          </div>
            {errors.smartphone && <span className="text-red-500 text-xs mt-1 block">{errors.smartphone}</span>}
          </div>

          {/* Accès Internet Mobile */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Avez-vous un accès Internet Mobile régulier ?</h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="accesInternetMobile_oui"
                name="accesInternetMobile"
                value="oui"
                checked={formData.accesInternetMobile === 'oui'}
                onChange={handleChange}
              />
              <label htmlFor="accesInternetMobile_oui">oui</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="accesInternetMobile_non"
                name="accesInternetMobile"
                value="non"
                checked={formData.accesInternetMobile === 'non'}
                onChange={handleChange}
              />
              <label htmlFor="accesInternetMobile_non" className="text-gray-700 cursor-pointer">Non</label>
            </div>
          </div>
          {errors.accesInternetMobile && <span className="text-red-500 text-xs mt-1 block">{errors.accesInternetMobile}</span>}
          </div>

          {/* Moyen de Déplacement */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Moyen de Déplacement principal pour les interventions</h2>

            <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="moyenDeplacement_moto"
                name="moyenDeplacement"
                value="moto"
                checked={formData.moyenDeplacement === 'moto'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="moyenDeplacement_moto" className="text-gray-700 cursor-pointer">Moto</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="moyenDeplacement_velo"
                name="moyenDeplacement"
                value="velo"
                checked={formData.moyenDeplacement === 'velo'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="moyenDeplacement_velo" className="text-gray-700 cursor-pointer">Vélo</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="moyenDeplacement_transport_public"
                name="moyenDeplacement"
                value="transport_public"
                checked={formData.moyenDeplacement === 'transport_public'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="moyenDeplacement_transport_public" className="text-gray-700 cursor-pointer">Transport public</label>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="radio"
                id="moyenDeplacement_autre"
                name="moyenDeplacement"
                value="autre"
                checked={formData.moyenDeplacement === 'autre'}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 focus:ring-blue-400"
              />
              <label htmlFor="moyenDeplacement_autre" className="text-gray-700 cursor-pointer">Other:</label>
              {formData.moyenDeplacement === 'autre' && (
                <input
                  type="text"
                  name="autreMoyenDeplacement"
                  value={formData.autreMoyenDeplacement}
                  onChange={handleChange}
                  className="ml-2 border-b border-gray-300 bg-transparent px-2 py-1 focus:outline-none focus:border-blue-400 transition-colors min-w-[200px]"
                  placeholder=""
                />
              )}
            </div>
          </div>
          {errors.moyenDeplacement && <span className="text-red-500 text-xs mt-1 block">{errors.moyenDeplacement}</span>}
          {formData.moyenDeplacement === 'autre' && errors.autreMoyenDeplacement && (
            <span className="text-red-500 text-xs mt-1 block">{errors.autreMoyenDeplacement}</span>
          )}
          </div>

          {/* Disponibilité Préférence */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 active:border-blue-400 active:ring-2 active:ring-blue-400 p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-blue-400">Disponibilité : Préférence de jours/heures (Cocher)</h2>

            <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="disponibilitePreference_joursOuvrables"
                name="disponibilitePreference_joursOuvrables"
                checked={formData.disponibilitePreference.joursOuvrables}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
              />
              <label htmlFor="disponibilitePreference_joursOuvrables" className="text-gray-700 cursor-pointer">jours ouvrables</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="disponibilitePreference_soirs"
                name="disponibilitePreference_soirs"
                checked={formData.disponibilitePreference.soirs}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
              />
              <label htmlFor="disponibilitePreference_soirs" className="text-gray-700 cursor-pointer">Soirs</label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="disponibilitePreference_weekend"
                name="disponibilitePreference_weekend"
                checked={formData.disponibilitePreference.weekend}
                onChange={handleChange}
                className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
              />
              <label htmlFor="disponibilitePreference_weekend" className="text-gray-700 cursor-pointer">weekend</label>
            </div>
          </div>
          {errors.disponibilitePreference && <span className="text-red-500 text-xs mt-1 block">{errors.disponibilitePreference}</span>}
          </div>

          {/* Captcha + soumission */}
          <div className="rounded-lg shadow-lg transition-all duration-200 p-6 md:p-8 text-center space-y-4">
            {CAPTCHA_SITE_KEY && IS_ENTERPRISE && (
              <RecaptchaWidget siteKey={CAPTCHA_SITE_KEY} />
            )}
            {CAPTCHA_SITE_KEY && IS_TURNSTILE && (
              <div className="flex justify-center">
                <TurnstileWidget siteKey={CAPTCHA_SITE_KEY} onToken={setCaptchaToken} />
              </div>
            )}
            {!CAPTCHA_SITE_KEY && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Captcha non configuré côté front (clé absente au build). Contactez l’administrateur.
              </p>
            )}
            <button 
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {submitting ? 'Envoi en cours…' : 'Soumettre ma candidature'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

